import type { ReportGrid } from '@palaccount/contracts';
import { ChevronDown, ChevronUp, Loader2, Printer, Sheet, FileDown, SlidersHorizontal, Table2 } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Amount } from '@/components/amount';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { roundToWholeUnits } from '@/lib/money';
import { useBaseCurrency, useCompanyToday, useMe } from '@/lib/session';
import { cn } from '@/lib/utils';

import { downloadReportPdf, printReportDocument } from './export';

/**
 * DSH-10 — the shared report skeleton: band 1 toolbar (Print / Excel / PDF +
 * period presets + the date pair), band 2 More filters (closed on load —
 * BR-DSH-10-08), band 3 the four summary cards, band 5 View detailed data
 * (closed on load, sideways-scrolling inside its own box — BR-DSH-10-12).
 */

export const PRESETS = ['today', 'thisWeek', 'thisMonth', 'thisQuarter', 'thisYear', 'custom'] as const;
export type PresetKey = (typeof PRESETS)[number];

/**
 * A period preset, anchored on a date the CALLER supplies — and the caller
 * supplies the COMPANY's today (N-16, EC-19-65: "there is exactly one today
 * across the whole system, read from the company's time zone").
 *
 * It used to read `new Date().toISOString().slice(0, 10)`, which is the UTC
 * date, while every document in the product is dated on the company clock. In
 * a UTC+3 tenant those two are a different DATE between local midnight and
 * 03:00 — so "this month to date" ended yesterday while today's invoices were
 * stamped today, and an accountant opening Sales Reports at 00:30 saw today's
 * invoices missing. No error, no empty state, just a smaller number than the
 * truth, for three hours a day.
 *
 * The `today` parameter is what makes that unrepresentable: this function has
 * no clock of its own to be wrong about. Every date below is derived from that
 * one string, read as a plain calendar date at UTC midnight — never from the
 * reader's machine.
 */
export function presetDates(
  preset: Exclude<PresetKey, 'custom'>,
  today: string,
): { from: string; to: string } {
  const to = today;
  const anchor = new Date(`${today}T00:00:00Z`);
  const y = anchor.getUTCFullYear();
  switch (preset) {
    case 'today':
      return { from: to, to };
    case 'thisWeek': {
      const day = (anchor.getUTCDay() + 6) % 7;
      return { from: new Date(anchor.getTime() - day * 86_400_000).toISOString().slice(0, 10), to };
    }
    case 'thisMonth':
      return { from: `${to.slice(0, 8)}01`, to };
    case 'thisQuarter': {
      const q = Math.floor(anchor.getUTCMonth() / 3) * 3;
      return { from: new Date(Date.UTC(y, q, 1)).toISOString().slice(0, 10), to };
    }
    case 'thisYear':
      return { from: `${y}-01-01`, to };
  }
}

/**
 * The window a report page opens on, and the two setters its date boxes write.
 *
 * The adoption effect is `useDocumentDate`'s, for the same reason: a report
 * page inside the shell always has a loaded session, but a page rendered
 * anywhere else does not, and the fallback in `useCompanyToday` is the
 * browser's date. So the window re-seeds when the company's today arrives —
 * and only while the reader has not moved it, after which the window is theirs.
 */
export function useReportWindow(initial: Exclude<PresetKey, 'custom'>): {
  today: string;
  from: string;
  to: string;
  setFrom: (value: string) => void;
  setTo: (value: string) => void;
} {
  const today = useCompanyToday();
  const seeded = presetDates(initial, today);
  const [from, setFrom] = useState(seeded.from);
  const [to, setTo] = useState(seeded.to);
  const seed = useRef(seeded);
  useEffect(() => {
    const next = presetDates(initial, today);
    const untouched = from === seed.current.from && to === seed.current.to;
    if (untouched && (next.from !== seed.current.from || next.to !== seed.current.to)) {
      seed.current = next;
      setFrom(next.from);
      setTo(next.to);
    }
  }, [today, initial, from, to]);
  return { today, from, to, setFrom, setTo };
}

/**
 * BAND 1's three export buttons, all fed from ONE grid.
 *
 * The `grid` a page passes here is the columns and rows it is displaying, in
 * the reader's language — which is what makes BR-DSH-10-24 ("the export
 * carries exactly the columns/rows on screen") true by construction rather
 * than by three call sites remembering to agree.
 *
 *  - PRINT renders that grid through `@palaccount/documents` into a print
 *    iframe. It no longer prints the SCREEN: paper and PDF are now the same
 *    artifact from the same template, which is the property DECISION.md §2.13
 *    bought when it chose Chromium.
 *  - EXCEL is the page's own handler, because only the page knows which of
 *    its columns are money and which are dates.
 *  - PDF asks the worker (§2.13: "PDF generation should be a job, not a
 *    request") and downloads the answer. The button spins while it waits,
 *    because the wait is real and pretending otherwise reads as a dead button.
 *
 * ERR-DSH-08-01 and ERR-DSH-08-09 are the prototype defect these three ended:
 * "Print, Excel and PDF have no handlers and do nothing" — three buttons drawn
 * in full colour that a reader could press all day. BR-DSH-08-22 is the
 * correction ("must each perform their action") and it is satisfied here
 * rather than on DSH-08's page, because the four report screens share one
 * toolbar and a per-screen fix would have left the next one to remember.
 */
/**
 * The period controls, or `null` on a report that declares itself as-of-now.
 *
 * BR-DSH-10-02 — *"Every report must accept a from date and a to date, and
 * every figure on the page must cover exactly that window"* — is what makes
 * this ONE optional group rather than seven independent props. A screen either
 * has a window and every figure honours it, or it has none; a toolbar that
 * could draw the date boxes while omitting the presets would make the
 * half-compliant state expressible, and the half-compliant state is exactly
 * the defect A14 was ruled to end. Passing `null` is the exemption, and it is
 * one word at the call site rather than four omitted props.
 */
export interface ReportWindowControls {
  presets: readonly PresetKey[];
  preset: PresetKey;
  onPreset: (p: PresetKey) => void;
  from: string;
  to: string;
  onFrom: (d: string) => void;
  onTo: (d: string) => void;
}

export function ReportToolbar({
  window: period,
  onExcel,
  grid,
}: {
  /** The period controls, or `null` — see `ReportWindowControls`. */
  window: ReportWindowControls | null;
  /** The page's own Excel handler — only it knows which columns are money. */
  onExcel: () => void;
  /** What is on screen. `null` while the report is still loading. */
  grid: ReportGrid | null;
}) {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  // The preset buttons compute a window; the anchor is the COMPANY's today,
  // never the browser's.
  const today = useCompanyToday();
  const [pdfState, setPdfState] = useState<'idle' | 'working' | 'failed'>('idle');
  const locale = i18n.language === 'ar' ? ('ar' as const) : ('en' as const);
  const company = { name: me?.tenant.name ?? '' };

  return (
    <div className="no-print flex flex-wrap items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        disabled={grid === null}
        onClick={() =>
          grid &&
          printReportDocument({ grid, locale, company, generatedBy: me?.user.name ?? null })
        }
      >
        <Printer className="me-1 h-4 w-4" />
        {t('reports.print')}
      </Button>
      <Button variant="outline" size="sm" onClick={onExcel}>
        <Sheet className="me-1 h-4 w-4" />
        {t('reports.excel')}
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={grid === null || pdfState === 'working'}
        title={pdfState === 'failed' ? t('reports.pdfFailed') : undefined}
        onClick={async () => {
          if (!grid) return;
          setPdfState('working');
          try {
            await downloadReportPdf(grid, locale);
            setPdfState('idle');
          } catch {
            // The row keeps the reason and the sweep keeps trying; what the
            // user needs here is to know the click did not work.
            setPdfState('failed');
          }
        }}
      >
        {pdfState === 'working' ? (
          <Loader2 className="me-1 h-4 w-4 animate-spin" />
        ) : (
          <FileDown className="me-1 h-4 w-4" />
        )}
        {t('reports.pdf')}
      </Button>
      {pdfState === 'failed' ? (
        <span role="alert" className="text-xs text-destructive">
          {t('reports.pdfFailed')}
        </span>
      ) : null}
      {period === null ? null : (
        <>
      <span className="mx-1 h-6 w-px bg-border" />
      {/*
        BR-DSH-10-03, both halves, and they are two halves of ONE loop of
        control rather than two features: *"Clicking a period preset must set
        both dates at once. Typing in either date must switch the preset row to
        Custom."*
        - FIRST HALF, the handler below: one click calls `onPreset`, `onFrom`
          and `onTo` in one pass. "At once" is the load-bearing phrase — a
          preset that set the from date and left the to date where the reader
          had dragged it would produce a window nobody chose, and the screen
          would still look like it was showing "This Quarter".
        - SECOND HALF, the two date inputs below: each `onChange` calls
          `onPreset('custom')` beside its own setter, so a typed date can never
          leave a preset button lit that no longer describes the window.
        The two must be written together or the pair oscillates: the preset
        writes dates, and if the date boxes reacted to that write by switching
        the row to Custom, no preset could ever stay selected. They do not,
        because `onChange` fires on the READER's edit and not on a programmatic
        value change — which is the whole reason this rule can be satisfied by
        two handlers that appear to fight.
      */}
      {period.presets.map((p) => (
        <Button
          key={p}
          size="sm"
          variant={period.preset === p ? 'default' : 'outline'}
          onClick={() => {
            period.onPreset(p);
            if (p !== 'custom') {
              const dates = presetDates(p, today);
              period.onFrom(dates.from);
              period.onTo(dates.to);
            }
          }}
        >
          {t(`reports.preset_${p}`)}
        </Button>
      ))}
      <div className="ms-auto flex items-center gap-1">
        <label className="text-xs text-muted-foreground" htmlFor="report-from">
          {t('reports.from')}
        </label>
        <input
          id="report-from"
          type="date"
          dir="ltr"
          className="rounded-md border bg-background px-2 py-1 text-sm"
          value={period.from}
          onChange={(e) => {
            period.onFrom(e.target.value);
            period.onPreset('custom');
          }}
        />
        <label className="text-xs text-muted-foreground" htmlFor="report-to">
          {t('reports.to')}
        </label>
        <input
          id="report-to"
          type="date"
          dir="ltr"
          className="rounded-md border bg-background px-2 py-1 text-sm"
          value={period.to}
          onChange={(e) => {
            period.onTo(e.target.value);
            period.onPreset('custom');
          }}
        />
      </div>
        </>
      )}
    </div>
  );
}

/** Band 2 — closed on load; a labelled grid of dropdowns inside. */
export function MoreFilters({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="no-print rounded-xl border">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium"
        onClick={() => setOpen((o) => !o)}
      >
        <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
        {t('reports.moreFilters')}
        {open ? <ChevronUp className="ms-auto h-4 w-4" /> : <ChevronDown className="ms-auto h-4 w-4" />}
      </button>
      {open ? <div className="grid gap-3 border-t p-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div> : null}
    </div>
  );
}

export interface SummaryCard {
  label: string;
  /** Preformatted value ("$51,831" arrives as "51831.00" + money flag, or a count). */
  value: string;
  money?: boolean;
  /** BR-DSH-10-06 pill; absent on DSH-07 (BR-DSH-07-03). */
  changePct?: string | undefined;
  /** DSH-07 §5b tints the turnover and dead-stock figures; the rest use default ink. */
  valueClassName?: string | undefined;
}

/**
 * BR-DSH-10-15, and BR-DSH-05-14 which repeats it for the sales report:
 * "Summary-card values must be ROUNDED TO WHOLE CURRENCY UNITS. Values inside
 * detail tables must show 2 DECIMALS." Both halves of one rule, and this
 * component owns the first half for all three report screens.
 *
 * The cards drew `<Amount>` — two decimals, like every table cell — so the
 * distinction the rule draws did not exist on screen. It is not decoration:
 * a summary card is read at a glance for magnitude, and `.00` on a
 * seven-figure total is two characters of noise on the one figure that most
 * needs to be read fast, while a detail row is read for reconciliation and
 * needs every cent.
 *
 * `roundToWholeUnits` is decimal end to end (N-6, half-up) and is the same
 * helper DSH-08's statement figures use, so a card and a statement round the
 * same number the same way. The DECIMALS change, never the value.
 */
export function SummaryCards({ cards }: { cards: SummaryCard[] }) {
  // GEN-107: what these figures are IN, from the session rather than a
  // literal. Every one of them is a ledger or stock valuation, which the
  // company holds in its base currency; nothing here converts, so GEN-116
  // keeps the disclosure off the screen.
  const baseCurrency = useBaseCurrency();
  const { t } = useTranslation();
  return (
    <div className="grid gap-[18px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' }}>
      {cards.map((card) => (
        <Card key={card.label}>
          <CardContent className="pt-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{card.label}</p>
            <p className={cn('mt-1 text-2xl font-semibold', card.valueClassName)}>
              {card.money ? (
                <Amount value={roundToWholeUnits(card.value)} currency={baseCurrency} decimals={0} />
              ) : (
                <bdi dir="ltr" className="font-mono tabular-nums">{card.value}</bdi>
              )}
            </p>
            {card.changePct !== undefined ? (
              <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                <bdi
                  dir="ltr"
                  className={cn(
                    'rounded-full px-1.5 py-0.5 font-medium',
                    card.changePct.startsWith('-') ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700',
                  )}
                >
                  {card.changePct.startsWith('-') ? t('unit.down') : t('unit.up')}{' '}
                  {t('unit.percent', { value: card.changePct })}
                </bdi>
                {t('reports.vsPrevious')}
              </p>
            ) : null}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * Band 5 — the fold. Closed on load; the wide table scrolls inside its box.
 *
 * ERR-DSH-05-02 is the row of §14's table that describes an EMPTY window and
 * asks for no error at all: "cards at $0 and 0, an empty trend, empty tables,
 * `0 RECORDS`." The count is the part worth naming — a fold that says nothing
 * about how much is inside it leaves the reader to open it to find out, and a
 * window with no invoices is not a fault, so nothing on the screen may treat
 * it as one. `t('reports.records', { count })` prints `0 records` for the same
 * reason it prints `500 records`: it is a fact, not a warning.
 */
export function DetailFold({ count, children }: { count: number; children: ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium"
        onClick={() => setOpen((o) => !o)}
      >
        <Table2 className="h-4 w-4 text-muted-foreground" />
        {t('reports.viewDetailed')}
        <span className="text-xs text-muted-foreground">{t('reports.records', { count })}</span>
        {open ? <ChevronUp className="ms-auto h-4 w-4" /> : <ChevronDown className="ms-auto h-4 w-4" />}
      </button>
      {open ? <div className="overflow-x-auto border-t p-3">{children}</div> : null}
    </div>
  );
}
